import { reportError } from "utils/diagnostics";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Dialog, DialogContent, DialogProps } from "@mui/material";
import {
  ArchiveDelivery,
  ConfigurationRestoreSupport,
  RestoreResult,
  getConfigurationRestoreSupport,
  pushFullConfigurationBackup,
} from "api/ConfigurationArchiveHelpers";
import Button from "@mui/material/Button";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import InputLabel from "@mui/material/InputLabel";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import { PopupHeader } from "../popup/popup-header.component";
import { GhostButton, PrimaryButton } from "../Buttons";
import { showError } from "utils/notifications";
import style from "../popup/popup.module.css";

dayjs.extend(utc);

const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;
const MAX_FULL_BACKUP_BYTES = 4 * 1024 * 1024;
const VALID_EXTENSIONS = [".tar.gz", ".tgz"];

interface PushConfigDialogProps extends Omit<DialogProps, "onClose"> {
  open: boolean;
  onClose: () => void;
  deviceCount: number;
  targetSerialNumbers: string[];
  isSubmitting?: boolean;
  onConfirm: (configFileBase64: string, configFileName: string, applyNow: boolean, scheduleTime: string) => void;
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve((reader.result as string).split(",")[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export function PushConfigDialog({
  open,
  onClose,
  deviceCount,
  targetSerialNumbers,
  isSubmitting = false,
  onConfirm,
}: PushConfigDialogProps): JSX.Element {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [archiveType, setArchiveType] = useState<"configuration" | "full">("configuration");
  const [delivery, setDelivery] = useState<ArchiveDelivery>("polling");
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState<RestoreResult>();
  const [error, setError] = useState("");
  const [support, setSupport] = useState<ConfigurationRestoreSupport & { targets: string }>();
  const targetKey = targetSerialNumbers.join(",");
  const currentSupport = support?.targets === targetKey ? support : undefined;
  const fullBackup = archiveType === "full";
  const busy = isSubmitting || processing;
  const generation = useRef(0);
  const [updateOption, setUpdateOption] = useState<"now" | "later">("now");
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(dayjs().add(1, "hour"));
  const [selectedHour, setSelectedHour] = useState<number>(() => {
    const nextHour = dayjs().add(1, "hour").hour();
    return nextHour % 12 || 12;
  });
  const [selectedAmPm, setSelectedAmPm] = useState<"AM" | "PM">(() => {
    const nextHour = dayjs().add(1, "hour").hour();
    return nextHour >= 12 ? "PM" : "AM";
  });
  const fileInputRef = useRef<HTMLInputElement>(null);

  const minDate = useMemo(() => dayjs().startOf("day"), []);
  const maxDate = useMemo(() => dayjs().add(7, "day").endOf("day"), []);

  const selectedDateTime = useMemo(() => {
    if (!selectedDate) return null;
    let hour24 = selectedHour;
    if (selectedAmPm === "PM" && selectedHour !== 12) {
      hour24 = selectedHour + 12;
    } else if (selectedAmPm === "AM" && selectedHour === 12) {
      hour24 = 0;
    }
    return selectedDate.hour(hour24).minute(0).second(0).millisecond(0);
  }, [selectedDate, selectedHour, selectedAmPm]);

  useEffect(() => {
    if (open) {
      const nextHour = dayjs().add(1, "hour");
      setSelectedDate(nextHour);
      const hour12 = nextHour.hour() % 12 || 12;
      setSelectedHour(hour12);
      setSelectedAmPm(nextHour.hour() >= 12 ? "PM" : "AM");
      setSelectedFile(null);
      setUpdateOption("now");
      setArchiveType("configuration");
      setDelivery("polling");
      setProcessing(false);
      setResult(undefined);
      setError("");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
    return () => {
      generation.current += 1;
    };
  }, [open, targetKey]);

  useEffect(() => {
    setSupport(undefined);
    if (!open || !fullBackup) return undefined;
    let active = true;
    const targets = targetKey ? targetKey.split(",") : [];
    if (!targets.length || targets.length > 100) {
      setSupport({ targets: targetKey, supported: false, reason: "Select 1–100 MegaFi 2 devices." });
    } else {
      getConfigurationRestoreSupport(targets)
        .then((availability) => {
          if (active) setSupport({ ...availability, targets: targetKey });
        })
        .catch((failure) => {
          if (!active) return;
          reportError("config.fullBackup.support", failure);
          setSupport({
            targets: targetKey,
            supported: false,
            reason: "Unable to check full-backup support. Close and reopen this dialog to retry.",
          });
        });
    }
    return () => {
      active = false;
    };
  }, [open, fullBackup, targetKey]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedFile(null);
    setError("");
    setResult(undefined);
    const file = event.target.files?.[0];
    if (!file) return;

    if (!VALID_EXTENSIONS.some((ext) => file.name.toLowerCase().endsWith(ext))) {
      showError("Please select a .tar.gz or .tgz file.");
      return;
    }
    if (file.size === 0) {
      showError("The selected file is empty.");
      return;
    }
    if (file.size > (fullBackup ? MAX_FULL_BACKUP_BYTES : MAX_FILE_SIZE_BYTES)) {
      showError(fullBackup ? "Full backups must be 4 MiB or smaller." : "File size must be under 5MB.");
      return;
    }

    setSelectedFile(file);
  };

  const handleConfirm = async () => {
    if (busy || result || (fullBackup && !currentSupport?.supported)) return;
    if (!selectedFile) {
      showError("Please upload a config archive.");
      return;
    }

    if (!fullBackup && updateOption === "later") {
      if (!selectedDateTime) {
        showError("Please select a date and time for scheduling.");
        return;
      }
      const now = dayjs();
      if (selectedDateTime.isBefore(now)) {
        showError("Please select a future date and time.");
        return;
      }
      const diffDays = selectedDateTime.diff(now, "day", true);
      if (diffDays > 7) {
        showError("The scheduled time must be within 7 days from now.");
        return;
      }
    }

    const { current } = generation;
    setProcessing(true);
    setError("");
    try {
      const base64 = await fileToBase64(selectedFile);
      if (current !== generation.current) return;
      if (fullBackup) {
        const response = await pushFullConfigurationBackup(base64, [...targetSerialNumbers], delivery);
        if (current === generation.current) setResult(response);
        return;
      }
      const scheduleTime =
        updateOption === "later" && selectedDateTime
          ? (selectedDateTime as any).utc().format("YYYY-MM-DD HH:mm:ss")
          : "";
      onConfirm(base64, selectedFile.name, updateOption === "now", scheduleTime);
    } catch (failure: any) {
      if (current !== generation.current) return;
      reportError("config.file.handleConfirm", failure);
      if (fullBackup) {
        setError(failure?.response?.data?.error || "Unable to push the full backup. Please retry.");
      } else {
        showError("Failed to read the selected file.");
      }
    } finally {
      if (current === generation.current) setProcessing(false);
    }
  };

  const handleClose = () => {
    if (busy) return;
    setSelectedFile(null);
    setUpdateOption("now");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    onClose();
  };

  return (
    <Dialog className={style.container} fullWidth maxWidth="sm" open={open} onClose={handleClose}>
      <PopupHeader title="Push Device Configuration" />
      <DialogContent>
        <div style={{ marginBottom: "24px" }}>
          <p style={{ margin: 0, fontSize: "16px" }}>
            Push configuration to {deviceCount} MegaFi 2 {deviceCount === 1 ? "device" : "devices"}.
          </p>
        </div>

        <FormControl component="fieldset" disabled={busy} style={{ marginBottom: "16px" }}>
          <RadioGroup
            aria-label="Archive type"
            value={archiveType}
            onChange={(event) => {
              setArchiveType(event.target.value as "configuration" | "full");
              setDelivery("polling");
              setSelectedFile(null);
              setUpdateOption("now");
              setResult(undefined);
              setError("");
              if (fileInputRef.current) fileInputRef.current.value = "";
            }}
          >
            <FormControlLabel value="configuration" control={<Radio />} label="Wi-Fi / GPS configuration" />
            <FormControlLabel value="full" control={<Radio />} label="Full device backup" />
          </RadioGroup>
        </FormControl>

        {fullBackup && (
          <>
            <p style={{ marginTop: 0 }}>
              Upload a full backup downloaded from the portal or saved from the device. Full backups can only be applied
              now.
            </p>
            <TextField
              id="full-backup-delivery"
              select
              label="Delivery"
              value={delivery}
              onChange={(event) => setDelivery(event.target.value as ArchiveDelivery)}
              disabled={busy || !currentSupport?.supported || !!result}
              fullWidth
              sx={{ mb: 2 }}
              helperText={
                delivery === "polling"
                  ? "Delivered when the device next checks in."
                  : delivery === "auto"
                  ? "Cloud selects MQTT or polling. MQTT restore requires compatible device firmware."
                  : "MQTT restore requires compatible device firmware."
              }
            >
              <MenuItem value="polling">Cloud polling</MenuItem>
              <MenuItem value="auto">Automatic</MenuItem>
              <MenuItem value="mqtt">MQTT</MenuItem>
            </TextField>
            {!currentSupport?.supported && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {currentSupport?.reason || "Checking full-backup support for the selected devices..."}
                {currentSupport && (
                  <div>
                    Remote full-backup restore needs compatible device firmware and cloud enablement. Selecting a file
                    does not enable it.
                  </div>
                )}
              </Alert>
            )}
          </>
        )}

        <div style={{ marginBottom: "24px" }}>
          <Button
            variant="outlined"
            component="label"
            startIcon={<CloudUploadIcon />}
            style={{ textTransform: "none" }}
            disabled={busy}
          >
            Upload config archive
            <input
              ref={fileInputRef}
              type="file"
              accept=".tar.gz,.tgz,application/gzip,application/x-gzip"
              hidden
              aria-label="Configuration archive file"
              disabled={busy}
              onChange={handleFileChange}
            />
          </Button>
          {selectedFile && (
            <p style={{ margin: "8px 0 0", fontSize: "14px", color: "#666" }}>{selectedFile.name} loaded.</p>
          )}
        </div>

        {!fullBackup && (
          <FormControl component="fieldset" disabled={busy} style={{ width: "100%", marginBottom: "24px" }}>
            <RadioGroup
              aria-label="push-option"
              name="push-option"
              value={updateOption}
              onChange={(event) => setUpdateOption(event.target.value as "now" | "later")}
            >
              <FormControlLabel value="now" control={<Radio />} label="Apply now" />
              <FormControlLabel value="later" control={<Radio />} label="Schedule for later time" />
            </RadioGroup>
          </FormControl>
        )}

        {!fullBackup && updateOption === "later" && (
          <div style={{ marginBottom: "24px" }}>
            <div style={{ marginBottom: "16px" }}>
              <DatePicker
                label="Select date"
                value={selectedDate}
                onChange={(newValue: Dayjs | null) => setSelectedDate(newValue)}
                minDate={minDate}
                maxDate={maxDate}
                slots={{ textField: TextField }}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </div>
            <div style={{ display: "flex", gap: "16px", alignItems: "flex-start" }}>
              <FormControl style={{ minWidth: "120px" }}>
                <InputLabel id="config-hour-label">Hour</InputLabel>
                <Select
                  labelId="config-hour-label"
                  id="config-hour-select"
                  value={selectedHour}
                  label="Hour"
                  onChange={(event: any) => setSelectedHour(Number(event.target.value))}
                >
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((hour) => (
                    <MenuItem key={hour} value={hour}>
                      {hour}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl style={{ minWidth: "120px" }}>
                <InputLabel id="config-ampm-label">AM/PM</InputLabel>
                <Select
                  labelId="config-ampm-label"
                  id="config-ampm-select"
                  value={selectedAmPm}
                  label="AM/PM"
                  onChange={(event: any) => setSelectedAmPm(event.target.value as "AM" | "PM")}
                >
                  <MenuItem value="AM">AM</MenuItem>
                  <MenuItem value="PM">PM</MenuItem>
                </Select>
              </FormControl>
            </div>
          </div>
        )}

        <div style={{ marginBottom: "24px", padding: "12px", backgroundColor: "#f5f5f5", borderRadius: "4px" }}>
          <p style={{ margin: 0, fontSize: "14px", color: "#666" }}>
            {fullBackup ? (
              <>
                The full backup will replace the saved configuration on the selected devices. Keep them powered on while
                it is applied.
              </>
            ) : (
              <>
                <strong>Note:</strong> Please keep the devices ON to apply the config. The update automatically reboots
                the device. The device will go offline for approximately 5-10 mins.
              </>
            )}
          </p>
        </div>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {result && (
          <Alert severity={result.results.some((item) => item.error) ? "warning" : "success"} sx={{ mb: 2 }}>
            {result.results.map((item) => (
              <div key={item.serialNumber}>
                {item.serialNumber}:{" "}
                {item.error || (item.delivery === "mqtt" ? "Sent via MQTT." : "Queued for cloud polling.")}
              </div>
            ))}
            Device restore completion is not confirmed here.
          </Alert>
        )}

        <div className={style.alignContentEvenly}>
          <div style={{ marginRight: "16px" }}>
            <GhostButton onClick={handleClose} disabled={busy}>
              {result ? "Close" : "Cancel"}
            </GhostButton>
          </div>
          <PrimaryButton
            onClick={handleConfirm}
            disabled={busy || !!result || (fullBackup && !currentSupport?.supported)}
          >
            {busy ? "Pushing..." : "Push Config"}
          </PrimaryButton>
        </div>
      </DialogContent>
    </Dialog>
  );
}
